<script setup lang="ts">

import ProgramSalesTimeline from "@/program/components/hospital-program-sales/ProgramSalesTimeline.vue";
import {computed} from "vue";
import {SupportProgramConsumerSettings} from "@/program/interfaces/Program";
import dateHelper from "@/core/helpers/dateHelper";

/**
 * The number of milliseconds in a day.
 */
const MILLISECONDS_PER_DAY = 1000 * 60 * 60 * 24;

export interface ConsumerProgramSale {
    datetime: string
    quantity: number
}

export interface ProgramSalesProps {
    sales: ConsumerProgramSale[]
    startAt: Date
}

const props = defineProps<ProgramSalesProps & { settings?: SupportProgramConsumerSettings }>();

const salesCount = computed(() => {
    return props.sales.reduce((acc, sale) => {
        return acc + (sale.quantity || 0);
    }, 0);
});

const salesFrequency = computed(() => {
    if (!Array.isArray(props.sales) || props.sales.length < 2) {
        return "N/A";
    }

    try {
        const salesDates = props.sales
            .map(sale => new Date(sale.datetime).getTime())
            .sort((a, b) => a - b);

        const totalTimeDiff = salesDates.reduce((acc, date, index) => {
            if (index === 0) return acc;
            const diff = date - salesDates[index - 1];
            return diff > 0 ? acc + diff : acc;
        }, 0);

        const averageDays = (totalTimeDiff / (salesDates.length - 1)) / MILLISECONDS_PER_DAY;
        return `~${Math.round(averageDays)} jours`;

    } catch {
        return "N/A";
    }
});

const lastSaleDate = computed(() => {
    if (!props.sales || props.sales?.length === 0) {
        return "N/A";
    }

    const lastSaleDatetime = props.sales![0].datetime;
    const date = new Date(lastSaleDatetime);
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - date.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    if (diffDays === 0) {
        return "Aujourd'hui";
    }
    const lastSaleDateFromNow = `(Il y a ${diffDays} jour${diffDays > 1 ? "s" : ""})`;

    return new Date(lastSaleDatetime).toLocaleDateString() + " " + lastSaleDateFromNow;
});
</script>

<template>
  <div class="bg-white shadow-md p-4 rounded-lg">
    <h3>Historique de délivrance</h3>
    <ProgramSalesTimeline :sales="sales" :start-at="startAt" />
    <div class="flex gap-x-6 gap-y-3 flex-wrap mt-4">
      <div v-if="!!settings?.options?.inclusion_date" class="text-gray-500 text-sm">
        Inclusion : <span class="font-medium">{{ dateHelper.shortFormat(settings.options.inclusion_date) }}</span>
      </div>
      <div v-if="!!settings?.options?.prescriptor" class="text-gray-500 text-sm">
        Prescripteur : <span class="font-medium">{{ settings.options.prescriptor }}</span>
      </div>
      <div class="text-gray-500 text-sm">
        Délivrances : <span class="font-medium">{{ salesCount }} {{ $filters.pluralize("unité", salesCount) }}</span>
      </div>
      <div class="text-gray-500 text-sm">
        Fréquence délivrance : <span class="font-medium">{{ salesFrequency }}</span>
      </div>
      <div class="text-gray-500 text-sm">
        Dernière délivrance : <span class="font-medium">{{ lastSaleDate }}</span>
      </div>
    </div>
  </div>
</template>

<style scoped>

</style>
